/**
 * Editable thresholds/weights for the LLM picker's ratings system.
 *
 * These constants control:
 *  - which models get flagged as "recommended" for Voice / Chat use cases
 *  - how the 0-5 speed/intelligence/cost stars are computed
 *
 * Star scale (per PRD §4.2):
 *  - Intelligence is ABSOLUTE (`index / 12`): "smart" is objective and the AA
 *    index already re-bases itself as the frontier moves.
 *  - Speed and Cost are RELATIVE (log10 min-max) to the full candidate catalog —
 *    not the currently-filtered view, so a star doesn't jump as the user narrows
 *    the filters — because "fast" and "cheap" only mean something against what
 *    you can actually pick today, and this auto-adapts when a better model
 *    appears. Log (not linear) because both metrics span orders of magnitude, so
 *    a single outlier would otherwise flatten everyone else.
 *  - Speed keeps absolute REALITY CLAMPS (in ms/tps, currency-independent) so
 *    "least-slow → 5★" can't happen in an all-slow catalog. Cost is purely
 *    relative: its price is in the org's currency, so a fixed USD clamp can't
 *    apply — cheapest available = 5★, priciest = 0★.
 *
 * Tweak these to retune without touching UI code. See
 * `src/pages/agents/model-ratings.ts`.
 */

export const STAR_MIN = 0;
export const STAR_MAX = 5;

/**
 * Voice ELIGIBILITY gate: a model can be recommended for voice only if it
 * responds fast enough (low TTFT) AND streams fast enough (high output tok/sec)
 * to feel real-time on a call. This is a hard requirement — no amount of
 * intelligence or cheapness recommends a slow model for a phone call. 500ms is
 * the outer bound of "feels real-time"; the weighted score then ranks the
 * eligible ones (see RECOMMENDATION_WEIGHTS).
 */
export const VOICE_GATE = {
  maxTtftMs: 500,
  minOutputTps: 40,
} as const;

/**
 * Chat ELIGIBILITY gate: smart enough to be a sane default. Price does NOT gate
 * here (an expensive-but-smart model is still eligible) — cost instead weighs
 * into the ranking score. Calibrated to the REAL Artificial Analysis Intelligence
 * Index, which saturates around ~60 for the frontier (NOT 0-100); 25 keeps
 * mid-tier and up, and the weighted score + cap trim to the best few.
 */
export const CHAT_GATE = {
  minIntelligenceIndex: 25,
} as const;

/**
 * Recommendation = ELIGIBILITY GATE (hard, above) + WEIGHTED SCORE (soft, here).
 * The eligible models are ranked by a weighted average of their three stars, so
 * the top of each intent's list balances the axes the way that intent cares
 * about. Weights per intent (speed / intelligence / cost):
 *  - voice: speed dominant; intelligence & cost only nudge among the fast.
 *  - chat: intelligence dominant; cost weighs in so "recommended" means a good
 *    value, not just the priciest frontier model (a slightly-less-smart, much
 *    cheaper model can rank above an expensive one).
 *  - all: balanced "best overall".
 */
export const RECOMMENDATION_WEIGHTS = {
  voice: { speed: 0.65, intelligence: 0.15, cost: 0.2 },
  chat: { speed: 0.15, intelligence: 0.65, cost: 0.2 },
  all: { speed: 0.3, intelligence: 0.45, cost: 0.25 },
} as const;

/**
 * A model missing a star (no harness speed, no rate-deck price) contributes this
 * NEUTRAL value to its weighted score for the missing axis — "unknown, assume
 * middling". Neither rewards the gap (as renormalising the weights would, which
 * let incomplete models leapfrog complete ones) nor crushes an otherwise-strong
 * model to zero. The real fix for a gap is to fill the data, not tune this.
 */
export const NEUTRAL_STAR = 2.5;

/**
 * Cap on how many models show under "Recommended". Recommendation only helps if
 * it's a shortlist — if almost everything is recommended, nothing is. The N best
 * (by the active intent's weighted score) are kept; the rest fall into the
 * provider groups.
 */
export const MAX_RECOMMENDED = 8;

/**
 * Intelligence star is absolute: `intelligence_index / this`, clamped 0-5.
 * Calibrated to the REAL Artificial Analysis Intelligence Index, whose frontier
 * saturates around ~53 after the AA re-based the index (2026-09; the old ~60
 * calibration left even the best model at ~4.4★) — so ÷10.5 maps the best
 * available model to 5★. Re-check this whenever `ingest-aa` refreshes a new
 * frontier. Future option (per Pedro): switch to relative-to-best normalisation
 * (top model = 5, rest scale from there) so this auto-recalibrates.
 */
export const INTELLIGENCE_STAR_DIVISOR = 10.5;

/**
 * Speed star — RELATIVE (min-max of TTFT + tokens/sec across the candidate
 * catalog), with absolute reality clamps so the number still tracks real-world
 * latency:
 *  - `floorTtftMs` + `floorMinTps`: a genuinely real-time model (fast to start
 *    AND streaming fast enough — i.e. voice-capable) never reads below
 *    `floorStar`. Keying the floor on tps too (not TTFT alone) keeps it aligned
 *    with the voice gate, so "fast enough to recommend" and "reads as fast" agree.
 *  - `ceilTtftMs`: above this TTFT it's too slow to look great → never above
 *    `ceilStar`, no matter how it ranks.
 * Between the two corners the relative blend provides the spread.
 */
export const SPEED_STAR = {
  ttftWeight: 0.7, // latency drives perceived responsiveness, esp. for voice
  tpsWeight: 0.3,
  floorTtftMs: 500, // aligned with VOICE_GATE.maxTtftMs
  floorMinTps: 50, // "genuinely real-time" bar for the display floor (stricter than the voice gate)
  floorStar: 3.5,
  ceilTtftMs: 1800,
  ceilStar: 3,
} as const;

/**
 * Weights to blend input/output token price (org currency per 1M) into a single
 * comparable figure. Input is weighted higher because prompts dominate. Cost is
 * PURELY relative (no absolute clamp) — see the header note.
 */
export const PRICE_BLEND_WEIGHTS = {
  input: 0.75,
  output: 0.25,
} as const;
